import { ExcelValidationError, NoSheetExistsError } from "../utils/ExcelValidationError";
import { fromOADate, getHeaderToIndexMap, getUsedRangeWithProperties, getRowsWithProperties } from "../utils/excel";
import { Ptc } from "./ptcParser";
import {
  ATIN_ID_COLUMN,
  ATIN_NAME_COLUMN,
  DEPRECATION_DATE_COLUMN,
  PTC_NA_COLUMN,
  STH_SHEET_REQUIRED_COLUMNS,
  TAXONOMY_EXPORT_REQUIRED_COLUMNS,
} from "../utils/constants";
import { parseSthTaxabilityPattern } from "../utils/parsers";

export interface Atin {
  readonly atinId: string;
  readonly atinName: string;
  readonly mappedPtc: Ptc;
  readonly deprecated: boolean;
  secondaryTaxability: string;
}

export const parseSthAtins = (
  taxonomyExportWorksheetName: string,
  sthTypeToJurisdictions: Map<string, string[]>,
  ptcs: Map<string, Ptc>,
  startDate: Date,
  setLoadingText: (loadingText: string) => void
) => {
  return Excel.run(async (context) => {
    const atinIdToAtin = await parseTaxonomyExport(
      context,
      taxonomyExportWorksheetName,
      ptcs,
      startDate,
      setLoadingText
    );

    for (const [sthType, jurisdictions] of sthTypeToJurisdictions) {
      const sthSheet = context.workbook.worksheets.getItemOrNullObject(sthType);
      await context.sync();

      if (sthSheet.isNullObject) {
        throw new ExcelValidationError(`STH Dates contains ${sthType} but missing worksheet for it`);
      }

      const usedRange = await getUsedRangeWithProperties(context, sthSheet, ["rowCount", "columnCount"]);

      const headerToIndex = await getHeaderToIndexMap(usedRange, context, sthType);
      for (const column of STH_SHEET_REQUIRED_COLUMNS) {
        if (!headerToIndex.has(column)) {
          throw new ExcelValidationError(`Missing column: ${column}`, sthType);
        }
      }
      for (const jurisdiction of jurisdictions) {
        if (!headerToIndex.has(jurisdiction)) {
          throw new ExcelValidationError(`Missing jurisdiction: ${jurisdiction}`, sthType);
        }
      }

      const atinIdsNotInSthSheet = new Set(atinIdToAtin.keys());

      const rows = await getRowsWithProperties(usedRange, context, ["values"]);
      for (let i = 1; i < rows.length; i++) {
        const row = rows[i];

        const atinId = row.values[0][headerToIndex.get(ATIN_ID_COLUMN)].toString().trim();
        if (!atinIdToAtin.has(atinId)) {
          throw new ExcelValidationError(`Atin ${atinId} not found in Taxonomy Export`, sthType, i + 1);
        }
        atinIdsNotInSthSheet.delete(atinId);

        const atin = atinIdToAtin.get(atinId);
        const sthJurisdictionColumnIndices = jurisdictions.map((jurisdiction) => headerToIndex.get(jurisdiction));
        parseSthTaxabilityPattern(atin, row, sthJurisdictionColumnIndices, sthType, i + 1);

        row.untrack();
      }

      if (atinIdsNotInSthSheet.size > 0) {
        throw new ExcelValidationError(`Following ATINs are not in the ${sthType} sheet, please make sure all ATINs from ${taxonomyExportWorksheetName} are in the STH sheets (leave cells empty if ATIN does not apply to the STH): ${Array.from(atinIdsNotInSthSheet)}`)
      }
    }
    return atinIdToAtin;
  });
};

const parseTaxonomyExport = async (
  context: Excel.RequestContext,
  worksheetName: string,
  ptcs: Map<string, Ptc>,
  startDate: Date,
  setLoadingText: (loadingText: string) => void
) => {
  setLoadingText("Reading ATINs from Taxonomy Export and STH sheets...");

  const atinIdToAtin = new Map<string, Atin>();

  const taxonomyExportSheet = context.workbook.worksheets.getItemOrNullObject(worksheetName);
  await context.sync();

  if (taxonomyExportSheet.isNullObject) {
    throw NoSheetExistsError(worksheetName);
  }

  const usedRange = await getUsedRangeWithProperties(context, taxonomyExportSheet, ["rowCount", "columnCount"]);

  const headerToIndex = await getHeaderToIndexMap(usedRange, context, worksheetName);
  for (const column of TAXONOMY_EXPORT_REQUIRED_COLUMNS) {
    if (!headerToIndex.has(column)) {
      throw new ExcelValidationError(`Missing column: ${column}`, worksheetName);
    }
  }

  const rows = await getRowsWithProperties(usedRange, context, ["values", "valueTypes"]);
  for (let i = 1; i < rows.length; i++) {
    const row = rows[i];

    const atinId = row.values[0][headerToIndex.get(ATIN_ID_COLUMN)].toString().trim();
    const atinName = row.values[0][headerToIndex.get(ATIN_NAME_COLUMN)].toString().trim();
    const ptcName = row.values[0][headerToIndex.get(PTC_NA_COLUMN)].toString().trim();

    if (!ptcName) {
      throw new ExcelValidationError(`Missing ${PTC_NA_COLUMN} field`, worksheetName, i + 1);
    }
    if (!ptcs.has(ptcName)) {
      throw new ExcelValidationError(`${ptcName} does not exist in TDM sheet`, worksheetName, i + 1);
    }

    let deprecated = false;
    const deprecationOaDate = row.values[0][headerToIndex.get(DEPRECATION_DATE_COLUMN)];
    const deprecationOaDateType = row.valueTypes[0][headerToIndex.get(DEPRECATION_DATE_COLUMN)];
    if (deprecationOaDate && deprecationOaDateType !== "Double") {
      throw new ExcelValidationError(
        `Invalid ${DEPRECATION_DATE_COLUMN} format, make sure cell type is Date`,
        worksheetName,
        i + 1
      );
    }
    if (deprecationOaDateType === "Double") {
      const deprecationDate = fromOADate(deprecationOaDate);
      deprecated = startDate >= deprecationDate;
    }

    const ptc = ptcs.get(ptcName);
    const atin: Atin = {
      atinId,
      atinName,
      deprecated,
      mappedPtc: ptc,
      secondaryTaxability: "",
    };
    ptc.atinsMapped.push(atin);

    atinIdToAtin.set(atinId, atin);
  }
  return atinIdToAtin;
};
